import { base64ToFile, getFileType } from "@/shared/lib";
import { Message, MessageFile } from "../model";
import { getTempMessagesDB } from "./temp-messages";
import { useAuthStore } from "@/features/auth";

const mimeToExt: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/ogg": "ogg",
  "application/pdf": "pdf",
  "text/plain": "txt",
};

export const processTempMessages = async (
  chatId: string,
  currentMessages: Message[]
): Promise<{ tempMessages: Message[]; allFiles: MessageFile[] }> => {
  const userId = useAuthStore.getState().user?._id;
  const tempMessagesDB = await getTempMessagesDB();
  const tempMessagesRaw = tempMessagesDB[chatId] ?? [];

  const existingTempIds = new Set(
    currentMessages.filter((m) => m.err).map((m) => m._id || m.text)
  );

  const tempMessages: Message[] = [];
  const allFiles: MessageFile[] = [];

  for (const tm of tempMessagesRaw) {
    if (tm.owner?._id !== userId) continue;

    const key = tm._id || tm.text;
    if (existingTempIds.has(key)) continue;

    const files: File[] = [];
    const fileUrls: string[] = [];

    for (let i = 0; i < (tm.filesBase64?.length ?? 0); i++) {
      const b64 = tm.filesBase64![i];
      const mimeMatch = b64.match(/^data:(.+?);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";

      const url = tm.fileUrls?.[i];
      const nameFromUrl = url?.split("/").pop() || "file";
      const [name, extRaw] = nameFromUrl.split(/\.(?=[^\.]+$)/);
      const ext =
        extRaw ||
        mimeToExt[mimeType] ||
        mimeType.split("/")[1]?.replace(/[^a-z0-9]/gi, "") ||
        "bin";

      const file = base64ToFile(b64, `${name}.${ext}`, mimeType);
      const objectUrl = URL.createObjectURL(file);
      fileUrls.push(objectUrl);

      if (["image", "video"].includes(getFileType(file))) {
        const time = tm.createdAt || tm.sentAt || Date.now();
        allFiles.push({ url: objectUrl, msgId: tm._id, createdAt: time });
      }

      files.push(file);
    }

    tempMessages.push({ ...tm, err: true, files, fileUrls });
  }

  return { tempMessages, allFiles };
};
